import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ClassMember, Class, User } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';

// 관련 학생 정보 타입 정의
interface RelatedStudentInfo {
  id: string;
  name: string;
  number: string;
  groupName?: string;
}

interface StudentTagSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedStudents: RelatedStudentInfo[]) => void; // 변경: ID 배열 대신 학생 정보 배열
  allStudents: ClassMember[];
  primaryStudentId: string; // 기본 선택된 멤버 (해제 불가)
  initialSelectedIds?: string[]; // 초기 선택된 멤버들
  currentClass?: Class | null; // 클래스 정보 (그룹 선택용)
  user?: User | null; // 사용자 정보 (프로플랜 체크용)
  userId?: string; // 사용자 ID (멤버 로드용)
  classId?: string; // 클래스 ID (멤버 로드용)
  forceLoadAllGroups?: boolean; // 모든 그룹 멤버 로드 강제 (기본 플랜에서도)
  currentGroup?: string; // 현재 선택된 그룹 (우선 표시할 그룹)
}

interface MemberWithGroup extends ClassMember {
  groupKey: string; // 멤버가 속한 그룹
}

const StudentTagSelectModal: React.FC<StudentTagSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  allStudents: initialAllStudents,
  primaryStudentId,
  initialSelectedIds = [],
  currentClass,
  user,
  userId,
  classId,
  forceLoadAllGroups = false,
  currentGroup
}) => {
  console.log('🎯 [StudentTagSelectModal] 컴포넌트 렌더링 - isOpen:', isOpen, 'forceLoadAllGroups:', forceLoadAllGroups);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [isGroupViewMode, setIsGroupViewMode] = useState<boolean>(false); // 그룹 모아보기 토글 (true: 모든 그룹 표시, false: 현재 그룹만 표시)
  const [allStudents, setAllStudents] = useState<MemberWithGroup[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // 스탠다드(2개 그룹), 프로/관리자(15개 그룹)일 때 그룹 선택 UI 표시
  const canUseMultipleMemberGroups = user?.userType === 'standard' || user?.userType === 'pro' || user?.userType === 'admin';
  
  // 멤버 그룹 목록 정규화
  const memberGroups = useMemo(() => {
    console.log('🔧 [StudentTagSelectModal] currentClass.memberGroups:', currentClass?.memberGroups);
    const result = getOrderedMemberGroups(currentClass?.memberGroups, currentClass?.memberGroupOrder);
    console.log('✅ [StudentTagSelectModal] 최종 memberGroups:', result);
    return result;
  }, [currentClass?.memberGroups, currentClass?.memberGroupOrder]);

  // 그룹 이름 가져오기
  const getGroupDisplayName = (groupKey: string): string => {
    // 설정된 그룹 이름이 있으면 사용, 없으면 "A그룹", "B그룹" 형식으로 표시
    return currentClass?.memberGroupNames?.[groupKey] || `${groupKey}그룹`;
  };

  // 멤버 그룹이 2개 이상이고 스탠다드/프로/관리자일 때 그룹 선택 UI 표시
  const showGroupSelector = canUseMultipleMemberGroups && memberGroups.length > 1 && userId && classId;

  // 그룹 모아보기 토글
  // 그룹 모아보기 토글
  const handleGroupViewModeToggle = () => {
    setIsGroupViewMode(!isGroupViewMode);
  };


  // 멤버 로드 (단일 그룹 또는 여러 그룹 모드)
  useEffect(() => {
    if (!isOpen || !userId || !classId) {
      const membersWithGroup = initialAllStudents.map(m => ({ ...m, groupKey: 'A' }));
      setAllStudents(membersWithGroup);
      return;
    }

    // forceLoadAllGroups가 true이면 무조건 모든 그룹 로드
    if (forceLoadAllGroups) {
      console.log('🎯 [StudentTagSelectModal] forceLoadAllGroups 활성화됨 - 모든 그룹 로드');
    } else if (!showGroupSelector) {
      console.log('⚠️ [StudentTagSelectModal] 그룹 선택 기능 없음 - 초기 멤버 사용');
      const membersWithGroup = initialAllStudents.map(m => ({ ...m, groupKey: 'A' }));
      setAllStudents(membersWithGroup);
      return;
    }

    const loadMembers = async () => {
      try {
        setIsLoadingMembers(true);

        if (forceLoadAllGroups) {
          // forceLoadAllGroups가 true이면 모든 그룹 로드
          const memberPromises = memberGroups.map(group =>
            userDataService.loadClassMembers(userId, classId, group).then(members => {
              console.log(`📋 [StudentTagSelectModal] ${group} 그룹 로드됨:`, members.length, '명');
              members.forEach(m => console.log(`  - ${group}: ${m.id} (${m.number}번 ${m.name})`));
              return members.map(m => ({ ...m, groupKey: group }));
            }).catch(error => {
              console.error(`❌ [StudentTagSelectModal] ${group} 그룹 로드 실패:`, error);
              return []; // 실패 시 빈 배열 반환
            })
          );

          const memberArrays = await Promise.all(memberPromises);

          // 멤버 병합 및 정렬
          const allMembers = memberArrays.flat().sort((a, b) => {
            // 그룹 순서대로 정렬, 같은 그룹 내에서는 번호순
            const groupA = memberGroups.indexOf(a.groupKey);
            const groupB = memberGroups.indexOf(b.groupKey);
            if (groupA !== groupB) return groupA - groupB;
            return parseInt(a.number) - parseInt(b.number);
          });

          console.log('🎯 [StudentTagSelectModal] 최종 allMembers:', allMembers.length, '명');
          allMembers.forEach(m => console.log(`  - ${m.groupKey}: ${m.id} (${m.number}번 ${m.name})`));

          setAllStudents(allMembers);
        } else {
          // forceLoadAllGroups가 false이면 초기 멤버 사용 (PointsTablePage 등)
          const membersWithGroup = initialAllStudents.map(m => ({
            ...m,
            groupKey: currentGroup || 'A' // currentGroup이 있으면 사용, 없으면 'A'
          }));
          setAllStudents(membersWithGroup);
        }
      } catch (error) {
        console.error('❌ [StudentTagSelectModal] 멤버 로드 실패:', error);
        const membersWithGroup = initialAllStudents.map(m => ({ ...m, groupKey: currentGroup || 'A' }));
        setAllStudents(membersWithGroup);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    loadMembers();
  }, [isOpen, showGroupSelector, forceLoadAllGroups, isGroupViewMode, userId, classId, memberGroups, initialAllStudents, currentGroup]);

  // 모달이 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      // 기본 멤버가 있고 초기 선택된 멤버들을 포함
      const initialSet = new Set<string>();
      
      // primaryStudentId가 유효한 경우에만 추가
      if (primaryStudentId && primaryStudentId.trim() !== '') {
        initialSet.add(primaryStudentId);
      }
      
      // 초기 선택된 멤버들 추가
      initialSelectedIds.forEach(id => {
        if (id && id.trim() !== '') {
          initialSet.add(id);
        }
      });
      
      setSelectedStudentIds(initialSet);

      // 그룹 모아보기 초기화 (기본적으로 꺼짐)
      setIsGroupViewMode(false);
    }
  }, [isOpen, primaryStudentId, initialSelectedIds, showGroupSelector, memberGroups]);

  const handleStudentToggle = (studentId: string) => {
    // 기본 멤버은 해제할 수 없음 (유효한 primaryStudentId인 경우에만)
    if (studentId === primaryStudentId && primaryStudentId && primaryStudentId.trim() !== '') return;

    setSelectedStudentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    // 빈 문자열이나 유효하지 않은 ID들을 필터링
    const validSelectedIds = Array.from(selectedStudentIds).filter(id => id && id.trim() !== '');

    // 선택된 학생들의 정보를 찾아서 RelatedStudentInfo 형태로 변환
    const selectedStudents: RelatedStudentInfo[] = validSelectedIds.map(id => {
      // allStudents에서 해당 학생 정보 찾기
      const student = allStudents.find(s => s.id === id);
      if (student) {
        return {
          id: student.id,
          name: student.name,
          number: student.number,
          groupName: getGroupDisplayName(student.groupKey || 'A') // 그룹 이름 표시
        };
      }
      // 찾을 수 없는 경우 기본값
      return {
        id: id,
        name: '알 수 없음',
        number: '??',
        groupName: '삭제됨'
      };
    });

    onConfirm(selectedStudents);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSelectAll = () => {
    // 현재 표시 모드에 따라 선택할 멤버 결정
    let membersToSelect: MemberWithGroup[];

    if (isGroupViewMode) {
      // 모아보기 모드: 모든 멤버 선택
      membersToSelect = allStudents;
    } else {
      // 일반 모드: 현재 표시되는 그룹의 멤버만 선택
      // currentGroup이 있으면 그것을 사용, 없으면 memberGroups[0]
      const currentGroupKey = currentGroup && memberGroups.includes(currentGroup)
        ? currentGroup
        : memberGroups[0] || 'A';
      membersToSelect = allStudents.filter(student => student.groupKey === currentGroupKey);
    }

    const allIds = new Set(membersToSelect.map(student => student.id));
    setSelectedStudentIds(allIds);
  };

  const handleDeselect = () => {
    // 기본 선택만 유지하고 나머지 모두 해제
    const defaultSet = new Set<string>();
    if (primaryStudentId && primaryStudentId.trim() !== '') {
      defaultSet.add(primaryStudentId);
    }
    setSelectedStudentIds(defaultSet);
  };

  // 선택 취소 버튼 텍스트 결정
  const getDeselectButtonText = () => {
    if (isGroupViewMode) {
      return '선택취소';
    } else {
      // 단일 그룹 선택 모드
      const currentGroupIds = new Set(allStudents.map(student => student.id));
      const hasSelectionInCurrentGroup = Array.from(currentGroupIds).some(id => 
        selectedStudentIds.has(id) && (id !== primaryStudentId || !primaryStudentId || primaryStudentId.trim() === '')
      );
      
      if (hasSelectionInCurrentGroup) {
        // 현재 그룹에 선택된 멤버가 있으면
        return '선택 취소';
      } else {
        // 현재 그룹에는 선택이 없지만 다른 곳에 선택이 있는 경우
        const hasSelectionElsewhere = selectedStudentIds.size > (primaryStudentId && primaryStudentId.trim() !== '' ? 1 : 0);
        if (hasSelectionElsewhere) {
          return '전체 선택 취소';
        } else {
          return '선택 취소';
        }
      }
    }
  };


  // 드롭다운 버튼 텍스트 결정

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>관련 멤버 선택</ModalTitle>
          <CloseButton onClick={handleCancel}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <Description>
            이 노트와 관련된 멤버들을 선택해주세요.
          </Description>

          {/* 그룹 모아보기 토글 */}
          {(showGroupSelector || forceLoadAllGroups) && memberGroups.length > 1 && (
            <GroupSelectSection>
              <GroupViewToggleRow>
                <GroupViewToggleLabel>모든 그룹 멤버 모아보기</GroupViewToggleLabel>
                <GroupViewToggleButton
                  type="button"
                  onClick={handleGroupViewModeToggle}
                  $isEnabled={isGroupViewMode}
                />
              </GroupViewToggleRow>
            </GroupSelectSection>
          )}
          
          <ActionButtonRow>
            <SelectAllButton onClick={handleSelectAll}>전체 선택</SelectAllButton>
            <DeselectButton onClick={handleDeselect}>
              {getDeselectButtonText()}
            </DeselectButton>
          </ActionButtonRow>

          {isLoadingMembers ? (
            <LoadingText>멤버 로딩 중...</LoadingText>
          ) : (
            <StudentList>
              {(() => {
                // 그룹별로 그룹핑
                const grouped: { [key: string]: MemberWithGroup[] } = {};
                allStudents.forEach(member => {
                  if (!grouped[member.groupKey]) {
                    grouped[member.groupKey] = [];
                  }
                  grouped[member.groupKey].push(member);
                });

                // 표시할 그룹들 결정
                const defaultGroup = currentGroup && memberGroups.includes(currentGroup)
                  ? currentGroup
                  : memberGroups[0] || 'A';
                const groupsToShow = isGroupViewMode ? memberGroups : [defaultGroup];

                // 그룹 순서대로 표시
                return groupsToShow.map(groupKey => {
                  const groupMembers = grouped[groupKey] || [];
                  if (groupMembers.length === 0) return null;

                  return (
                    <React.Fragment key={groupKey}>
                      {isGroupViewMode && (
                        <GroupHeader>{getGroupDisplayName(groupKey)}</GroupHeader>
                      )}
                      {groupMembers.map(student => {
                        const isSelected = selectedStudentIds.has(student.id);
                        const isPrimary = student.id === primaryStudentId;

                        return (
                          <StudentItem
                            key={student.id}
                            $isSelected={isSelected}
                            $isPrimary={isPrimary}
                            onClick={() => handleStudentToggle(student.id)}
                          >
                            <StudentCheckbox
                              type="checkbox"
                              checked={isSelected}
                              disabled={!!(isPrimary && primaryStudentId && primaryStudentId.trim() !== '')}
                              readOnly
                            />
                            <StudentInfo>
                              <StudentNumber>{student.number}번</StudentNumber>
                              <StudentName>{student.name}</StudentName>
                              {isPrimary && primaryStudentId && primaryStudentId.trim() !== '' && <PrimaryLabel>기본</PrimaryLabel>}
                            </StudentInfo>
                          </StudentItem>
                        );
                      })}
                    </React.Fragment>
                  );
                });
              })()}
            </StudentList>
          )}
        </ModalBody>
        
        <ModalFooter>
          <CancelButton data-variant="modal-secondary" onClick={handleCancel}>취소</CancelButton>
          <ConfirmButton onClick={handleConfirm}>
            확인 ({Array.from(selectedStudentIds).filter(id => id && id.trim() !== '' && id !== primaryStudentId).length}명 선택)
          </ConfirmButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-gray);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background: var(--color-surface-highlight);
    color: var(--color-text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  overflow-y: auto;
`;

const Description = styled.p`
  margin: 0 0 16px 0;
  color: var(--color-text-gray);
  font-size: 14px;
  line-height: 1.4;
`;

const GroupSelectSection = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
`;

const GroupViewToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
  width: 100%;
`;

const GroupViewToggleLabel = styled.span`
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: center;
`;

const GroupViewToggleButton = styled.button<{ $isEnabled: boolean }>`
  position: relative;
  width: 70px;
  height: 28px;
  min-width: 70px;
  min-height: 28px;
  border-radius: 14px;
  background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  padding: 0;
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  -webkit-focus-ring-color: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  
  &::before {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.$isEnabled ? '34px' : '3px'};
    width: 34px;
    height: 22px;
    background: var(--color-surface);
    border-radius: 11px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &::after {
    content: '${props => props.$isEnabled ? 'ON' : 'OFF'}';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    font-weight: bold;
    color: ${props => props.$isEnabled ? 'var(--color-toggle-on-text)' : 'var(--color-toggle-off-text)'};
    left: ${props => props.$isEnabled ? '34px' : '3px'};
    width: 34px;
    text-align: center;
    transition: all 0.3s ease;
    line-height: 1;
    pointer-events: none;
  }
  
  &:hover {
    background: ${props => props.$isEnabled ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.div`
  padding: 8px 12px;
  background: var(--color-panel);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-top: 8px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: var(--color-text-gray);
  font-size: 14px;
`;

const ActionButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
`;

const SelectAllButton = styled.button`
  flex: 1;
  padding: 8px 16px;
  border: 1px solid rgba(var(--color-accent-rgb), 0.5);
  background: var(--color-surface);
  color: var(--color-accent-text);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: var(--color-accent-soft);
    border-color: var(--color-accent-text);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DeselectButton = styled.button`
  flex: 1;
  padding: 8px 16px;
  border: 1px solid rgba(var(--color-accent-rgb), 0.5);
  background: var(--color-surface);
  color: var(--color-accent-text);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: var(--color-accent-soft);
    border-color: var(--color-accent-text);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentItem = styled.div<{ $isSelected: boolean; $isPrimary: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 2px solid ${props => 
    props.$isSelected ? 'rgba(var(--color-accent-rgb), 0.5)' : 'var(--color-border)'
  };
  border-radius: 8px;
  cursor: ${props => props.$isPrimary ? 'not-allowed' : 'pointer'};
  background: ${props => 
    props.$isSelected ? 'var(--color-student-item-selected-bg)' : 'transparent'
  };
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => 
      props.$isSelected ? 'var(--color-student-item-selected-bg)' : 'var(--color-panel)'
    };
  }
`;

const StudentCheckbox = styled.input`
  margin-right: 12px;
  width: 18px;
  height: 18px;
  cursor: inherit;
  accent-color: var(--color-accent-text); /* 메인 테마 초록색 */
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const StudentNumber = styled.span`
  font-size: 14px;
  color: var(--color-text-gray);
  font-weight: 500;
`;

const StudentName = styled.span`
  font-size: 16px;
  color: var(--color-text-primary);
  font-weight: 600;
`;

const PrimaryLabel = styled.span`
  background: var(--color-accent-strong);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-gray);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: var(--color-panel);
    border-color: #bbb;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 10px 20px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: var(--color-accent);
  }
`;

export default StudentTagSelectModal;
